/**
 * The right panel: the item's four slots as drop zones. Each lists the columns
 * in it (draggable on to another zone) with the field name each becomes, and a
 * remove button that sends a column back to "skipped".
 */

import { useState, type DragEvent } from "react";
import { X } from "lucide-react";
import { Pill } from "@/components/ui/pill";
import { SLOTS, fieldName, keyColumn, slotOf, type Mapping, type Slot } from "../csv-mapping";
import { SLOT_HINT, TARGET_LABEL } from "./targets";

interface Props {
  mapping: Mapping;
  onDrop: (slot: Slot) => void;
  onRemove: (index: number) => void;
  onDragStart: (index: number, e: DragEvent) => void;
}

function Zone({ slot, mapping, onDrop, onRemove, onDragStart }: Props & { slot: Slot }) {
  const [over, setOver] = useState(false);
  const title = TARGET_LABEL[slot];
  const key = keyColumn(mapping.columns);
  const members = mapping.columns.map((c, i) => ({ ...c, i })).filter((c) => slotOf(c.target) === slot);
  const missing = slot === "input" && members.length === 0;
  return (
    <div
      role="group"
      aria-label={title}
      onDragOver={(e) => (e.preventDefault(), setOver(true))}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => (e.preventDefault(), e.stopPropagation(), setOver(false), onDrop(slot))}
      className={`flex flex-col gap-2 rounded-lg border-2 border-dashed p-3 ${
        over ? "border-link bg-drop-bg" : missing ? "border-warn" : "border-line"
      }`}
    >
      <div className="flex flex-col gap-0.5">
        <span className="flex items-center gap-2 text-[13px] font-semibold">
          {title}
          {slot === "input" && <span className="text-xs font-normal text-muted">required</span>}
        </span>
        <span className="text-xs text-muted">{SLOT_HINT[slot]}</span>
      </div>
      <ul className="flex flex-wrap gap-1.5">
        {members.map((c) => {
          const field = fieldName(c.column, c.target);
          return (
            <li
              key={`${c.column}-${c.i}`}
              draggable
              onDragStart={(e) => onDragStart(c.i, e)}
              className="flex cursor-grab items-center gap-1.5 rounded-md border border-line bg-surface py-1 pr-1 pl-2 text-xs"
            >
              <span className="font-mono">{c.column}</span>
              {field !== c.column && <span className="font-mono text-muted">→ {field}</span>}
              {c.i === key && <Pill className="text-[11px]">item key</Pill>}
              <button
                type="button"
                aria-label={`Remove ${c.column} from ${title}`}
                className="rounded p-0.5 text-faint hover:bg-chip hover:text-ink"
                onClick={() => onRemove(c.i)}
              >
                <X size={12} aria-hidden />
              </button>
            </li>
          );
        })}
        {members.length === 0 && (
          <li className={`py-1 text-xs ${missing ? "text-warn" : "text-faint"}`}>
            {missing ? "Drag at least one column here." : "Drag columns here."}
          </li>
        )}
      </ul>
    </div>
  );
}

export function SlotZones(props: Props) {
  return (
    <section aria-label="Item slots" className="flex min-w-0 flex-col gap-3">
      {SLOTS.map((slot) => (
        <Zone key={slot} slot={slot} {...props} />
      ))}
    </section>
  );
}
